import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Home, Search, Plus, Edit3, Trash2, MapPin, Camera, Video, MoreHorizontal, EyeOff } from 'lucide-react';
import EmptyState from '../../../components/ui/EmptyState';

const PropertyManagement = ({ onStatsUpdate }) => {
  const [properties, setProperties] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    property_type: 'all',
    commune_id: 'all',
    agent_id: 'all'
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const statusOptions = [
    { value: 'available', label: 'Disponible', color: 'bg-green-100 text-green-800' },
    { value: 'reserved', label: 'Réservé', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'sold', label: 'Vendu', color: 'bg-blue-100 text-blue-800' },
    { value: 'withdrawn', label: 'Retiré', color: 'bg-red-100 text-red-800' },
    { value: 'hidden', label: 'Masqué', color: 'bg-gray-100 text-gray-600' }
  ];

  const typeOptions = [
    { value: 'apartment', label: 'Appartement' },
    { value: 'house', label: 'Maison' },
    { value: 'land', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'villa', label: 'Villa' }
  ];

  const fetchProperties = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('properties')?.select(`
        *,
        commune:communes(id, name, district),
        agent:user_profiles!agent_id(id, full_name, email),
        owner:user_profiles!owner_id(id, full_name, email)
      `)?.order('created_at', { ascending: false });

      if (filters?.status !== 'all') query = query?.eq('status', filters?.status);
      if (filters?.property_type !== 'all') query = query?.eq('property_type', filters?.property_type);
      if (filters?.commune_id !== 'all') query = query?.eq('commune_id', filters?.commune_id);
      if (filters?.agent_id !== 'all') query = query?.eq('agent_id', filters?.agent_id);

      const { data, error } = await query;
      if (error) throw error;
      setProperties(data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      alert('Erreur lors du chargement des propriétés');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterData = async () => {
    try {
      const [communesRes, agentsRes] = await Promise.all([
        supabase?.from('communes')?.select('id, name, district')?.order('name'),
        supabase?.from('user_profiles')?.select('id, full_name')?.eq('role', 'agent')?.order('full_name')
      ]);
      setCommunes(communesRes?.data || []);
      setAgents(agentsRes?.data || []);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => { fetchProperties(); }, [filters]);
  useEffect(() => { fetchFilterData(); }, []);

  const filteredProperties = properties?.filter(property => 
    property?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    property?.address?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    property?.commune?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette propriété ?')) return;
    try {
      const { error } = await supabase?.from('properties')?.delete()?.eq('id', propertyId);
      if (error) throw error;
      fetchProperties();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error deleting property:', error);
      alert('Erreur lors de la suppression de la propriété');
    }
  };

  const handleStatusChange = async (propertyId, newStatus) => {
    try {
      const { error } = await supabase?.from('properties')?.update({ status: newStatus })?.eq('id', propertyId);
      if (error) throw error;
      fetchProperties();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusColor = (status) => {
    const option = statusOptions?.find(opt => opt?.value === status);
    return option?.color || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const option = statusOptions?.find(opt => opt?.value === status);
    return option?.label || status;
  };

  const getTypeLabel = (type) => {
    const option = typeOptions?.find(opt => opt?.value === type);
    return option?.label || type;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' })?.format(price);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="bg-card border border-border p-6 rounded-lg animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-muted rounded-lg"></div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-card border border-border p-4 sm:p-6 rounded-lg">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Home className="w-5 h-5 text-brand-gold flex-shrink-0" />
                Gestion des Propriétés
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {filteredProperties?.length} propriété(s) trouvée(s)
              </p>
            </div>
            
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-earth text-white rounded-lg hover:bg-brand-earth/90 transition-colors min-h-[44px] touch-manipulation text-sm font-medium w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span>Ajouter Propriété</span>
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher propriétés..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
            />
          </div>

          {/* Filters — responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Statut</label>
              <select
                value={filters?.status}
                onChange={(e) => setFilters({...filters, status: e?.target?.value})}
                className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              >
                <option value="all">Tous les statuts</option>
                {statusOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Type</label>
              <select
                value={filters?.property_type}
                onChange={(e) => setFilters({...filters, property_type: e?.target?.value})}
                className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              >
                <option value="all">Tous les types</option>
                {typeOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Commune</label>
              <select
                value={filters?.commune_id}
                onChange={(e) => setFilters({...filters, commune_id: e?.target?.value})}
                className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              >
                <option value="all">Toutes les communes</option>
                {communes?.map(commune => (
                  <option key={commune?.id} value={commune?.id}>
                    {commune?.name} ({commune?.district})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Agent</label>
              <select
                value={filters?.agent_id}
                onChange={(e) => setFilters({...filters, agent_id: e?.target?.value})}
                className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              >
                <option value="all">Tous les agents</option>
                {agents?.map(agent => (
                  <option key={agent?.id} value={agent?.id}>{agent?.full_name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Properties Grid — responsive: 1 col mobile → 2 cols lg → 3 cols xl */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
        {filteredProperties?.map((property) => (
          <div key={property?.id} className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-warm transition-all duration-200">
            {/* Property Image */}
            <div className="relative h-44 sm:h-48 bg-muted">
              {property?.images && property?.images?.length > 0 ? (
                <img
                  src={property?.images?.[0]}
                  alt={property?.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Home className="w-12 h-12 text-muted-foreground" />
                </div>
              )}
              
              {/* Status Badge */}
              <div className="absolute top-3 left-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(property?.status)}`}>
                  {property?.status === 'hidden' && <EyeOff className="w-3 h-3" />}
                  {getStatusLabel(property?.status)}
                </span>
              </div>

              {/* Media Icons */}
              <div className="absolute top-3 right-3 flex space-x-1.5">
                {property?.images && property?.images?.length > 0 && (
                  <div className="bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                    <Camera className="w-3 h-3" />
                    {property?.images?.length}
                  </div>
                )}
                {property?.videos && property?.videos?.length > 0 && (
                  <div className="bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                    <Video className="w-3 h-3" />
                    {property?.videos?.length}
                  </div>
                )}
              </div>
            </div>

            {/* Property Details */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-base font-semibold text-foreground truncate flex-1">
                  {property?.title}
                </h3>
                <button className="text-muted-foreground hover:text-foreground p-1 ml-2 flex-shrink-0 min-w-[32px] min-h-[32px] flex items-center justify-center touch-manipulation">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center text-sm text-muted-foreground mb-2">
                <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                <span className="truncate">{property?.address}</span>
              </div>

              <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                <span>{getTypeLabel(property?.property_type)}</span>
                <span>{property?.commune?.name}</span>
              </div>

              <div className="flex items-center justify-between mb-3">
                <div className="text-base font-bold text-foreground">
                  {formatPrice(property?.price)}
                </div>
                {property?.surface_area && (
                  <div className="text-sm text-muted-foreground">
                    {property?.surface_area}m²
                  </div>
                )}
              </div>

              {(property?.bedrooms || property?.bathrooms) && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                  {property?.bedrooms && <span>{property?.bedrooms} ch.</span>}
                  {property?.bathrooms && <span>{property?.bathrooms} SdB</span>}
                </div>
              )}

              {property?.agent && (
                <div className="text-xs text-muted-foreground mb-3">
                  Agent: {property?.agent?.full_name}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <select
                  value={property?.status}
                  onChange={(e) => handleStatusChange(property?.id, e?.target?.value)}
                  className="text-xs px-2 py-1.5 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-transparent min-h-[36px] touch-manipulation"
                >
                  {statusOptions?.map(option => (
                    <option key={option?.value} value={option?.value}>{option?.label}</option>
                  ))}
                </select>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditingProperty(property); setShowAddModal(true); }}
                    className="text-brand-earth hover:text-brand-earth/80 p-2 rounded-lg hover:bg-brand-earth/10 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center touch-manipulation"
                    aria-label="Modifier"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteProperty(property?.id)}
                    className="text-destructive hover:text-destructive/80 p-2 rounded-lg hover:bg-destructive/10 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center touch-manipulation"
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProperties?.length === 0 && (
        <div className="bg-card border border-border rounded-xl">
          <EmptyState
            iconName="Home"
            title="Aucune propriété trouvée"
            description="Aucune propriété ne correspond à vos critères. Ajoutez une nouvelle propriété pour commencer."
            ctaLabel="Ajouter une propriété"
            ctaIcon="Plus"
            onCta={() => setShowAddModal(true)}
          />
        </div>
      )}
    </div>
  );
};

export default PropertyManagement;